import { useState, useEffect, useCallback } from 'react';
import { Storage } from 'aws-amplify';
import { getVideoData } from '../../../graphql/queries';
import { API } from 'aws-amplify';
import { 
  normalizeVideoData, 
  normalizeTelemetryData, 
  mergeLapData, 
  calculateCapabilities 
} from '../utils/dataTransforms';

/**
 * Primary hook for loading and normalizing video session data
 * Handles VideoData fetching, telemetry loading, and data normalization
 * 
 * @param {string} videoId - VideoData ID to load
 * @returns {Object} { sessionData, isLoading, error, reload }
 */
export function useVideoSession(videoId) {
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load VideoData from database
   */
  const loadVideoData = useCallback(async (id) => {
    try {
      const response = await API.graphql({
        query: getVideoData,
        variables: { id }
      });
      
      return response.data.getVideoData;
    } catch (err) {
      console.error('Failed to load VideoData:', err);
      throw new Error(`Failed to load video data: ${err.message}`);
    }
  }, []);

  /**
   * Load telemetry data from S3, local URL, or trackside URL
   */
  const loadTelemetryData = useCallback(async (telemetryS3Key, tempLocalTelemetryLink, tracksideTelemetryUrl) => {
    if (!telemetryS3Key && !tempLocalTelemetryLink && !tracksideTelemetryUrl) return null;
    
    try {
      let jsonData;
      
      if (telemetryS3Key) {
        // Load from S3 (highest priority)
        console.log('📡 Loading telemetry from S3:', telemetryS3Key);
        const result = await Storage.get(telemetryS3Key, {
          download: true,
          level: 'public'
        });
        const textData = await result.Body.text();
        jsonData = JSON.parse(textData);
      } else if (tracksideTelemetryUrl) {
        // Load from trackside server (second priority)
        console.log('📡 Loading telemetry from trackside URL:', tracksideTelemetryUrl);
        const response = await fetch(tracksideTelemetryUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        jsonData = await response.json();
      } else if (tempLocalTelemetryLink) {
        // Load from local URL (lowest priority, for development)
        console.log('📡 Loading telemetry from local URL:', tempLocalTelemetryLink);
        const response = await fetch(tempLocalTelemetryLink);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        jsonData = await response.json();
      }
      
      return normalizeTelemetryData(jsonData);
    } catch (err) {
      console.error('Failed to load telemetry data:', err);
      // Don't throw here - telemetry is optional
      return null;
    }
  }, []);

  /**
   * Load segment definitions based on track/venue
   * TODO: Move this to a separate utility when segment system is finalized
   */
  const loadSegmentDefinitions = useCallback(async (trackId, trackName) => {
    // For now, return null - segments will be added in future iterations
    // This is where we'll load track-specific segment definitions
    return null;
  }, []);

  /**
   * Main data loading function
   */
  const loadSessionData = useCallback(async (id) => {
    if (!id) {
      setSessionData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Load VideoData from database
      const videoData = await loadVideoData(id);
      if (!videoData) {
        throw new Error('Video data not found');
      }

      console.log('🔍 Raw VideoData from GraphQL:', videoData);
      console.log('📊 VideoData.laptimeData:', videoData.laptimeData);

      // Step 2: Normalize VideoData into base session object
      const normalizedSession = normalizeVideoData(videoData);
      console.log('✅ Normalized session data:', normalizedSession);

      // Step 3: Load telemetry data if available
      let telemetryData = null;
      if (videoData.telemetryS3Key || videoData.tempLocalTelemetryLink || videoData.tracksideTelemetryUrl) {
        telemetryData = await loadTelemetryData(
          videoData.telemetryS3Key, 
          videoData.tempLocalTelemetryLink, 
          videoData.tracksideTelemetryUrl
        );
      }

      // Step 4: Load segment definitions
      const segments = await loadSegmentDefinitions(
        videoData.trackId, 
        normalizedSession.metadata.trackName
      );

      // Step 5: Merge lap data from multiple sources
      const videoLapData = normalizedSession.lapData;
      const telemetryLapData = telemetryData?.laps || [];
      const mergedLapData = mergeLapData(
        videoLapData,
        telemetryLapData,
        normalizedSession.crossStart
      );

      // Step 6: Create final session object
      const finalSession = {
        ...normalizedSession,
        lapData: mergedLapData,
        telemetry: telemetryData,
        segments,
        capabilities: calculateCapabilities({
          lapData: mergedLapData,
          telemetry: telemetryData,
          segments
        })
      };

      setSessionData(finalSession);
    } catch (err) {
      console.error('Failed to load session data:', err);
      setError(err);
      setSessionData(null);
    } finally {
      setIsLoading(false);
    }
  }, [loadVideoData, loadTelemetryData, loadSegmentDefinitions]);

  /**
   * Reload session data
   */
  const reload = useCallback(() => {
    loadSessionData(videoId);
  }, [loadSessionData, videoId]);

  // Load data when videoId changes
  useEffect(() => {
    loadSessionData(videoId);
  }, [loadSessionData, videoId]);

  return {
    sessionData,
    isLoading,
    error,
    reload
  };
}

/**
 * Hook for loading multiple video sessions (for comparison view)
 * 
 * @param {Array} videoIds - Array of VideoData IDs to load
 * @returns {Object} { sessions, isLoading, errors, reload }
 */
export function useMultipleVideoSessions(videoIds = []) {
  const [sessions, setSessions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const loadSession = useCallback(async (videoId) => {
    try {
      setIsLoading(true);
      
      // Use the single session hook logic
      const videoData = await API.graphql({
        query: getVideoData,
        variables: { id: videoId }
      });

      if (!videoData.data.getVideoData) {
        throw new Error('Video data not found');
      }

      const normalizedSession = normalizeVideoData(videoData.data.getVideoData);
      
      // Load telemetry if available
      let telemetryData = null;
      if (videoData.data.getVideoData.telemetryS3Key) {
        try {
          const result = await Storage.get(videoData.data.getVideoData.telemetryS3Key, {
            download: true,
            level: 'public'
          });
          const textData = await result.Body.text();
          const jsonData = JSON.parse(textData);
          telemetryData = normalizeTelemetryData(jsonData);
        } catch (telErr) {
          console.warn(`Failed to load telemetry for ${videoId}:`, telErr);
        }
      }

      // Merge lap data
      const mergedLapData = mergeLapData(
        normalizedSession.lapData,
        telemetryData?.laps || [],
        normalizedSession.crossStart
      );

      const finalSession = {
        ...normalizedSession,
        lapData: mergedLapData,
        telemetry: telemetryData,
        segments: null, // TODO: Add segments
        capabilities: calculateCapabilities({
          lapData: mergedLapData,
          telemetry: telemetryData,
          segments: null
        })
      };

      setSessions(prev => ({
        ...prev,
        [videoId]: finalSession
      }));

      setErrors(prev => {
        const { [videoId]: removed, ...rest } = prev;
        return rest;
      });

    } catch (err) {
      console.error(`Failed to load session ${videoId}:`, err);
      setErrors(prev => ({
        ...prev,
        [videoId]: err
      }));
    }
  }, []);

  const loadAllSessions = useCallback(async () => {
    if (!videoIds || videoIds.length === 0) {
      setSessions({});
      setErrors({});
      return;
    }

    setIsLoading(true);
    
    // Load all sessions in parallel
    const loadPromises = videoIds.map(videoId => loadSession(videoId));
    
    try {
      await Promise.allSettled(loadPromises);
    } finally {
      setIsLoading(false);
    }
  }, [videoIds, loadSession]);

  const reload = useCallback(() => {
    loadAllSessions();
  }, [loadAllSessions]);

  // Load sessions when videoIds change
  useEffect(() => {
    loadAllSessions();
  }, [loadAllSessions]);

  return {
    sessions,
    isLoading,
    errors,
    reload
  };
}

/**
 * Hook for getting session statistics and metadata
 * 
 * @param {Object} sessionData - Normalized session data
 * @returns {Object} Session statistics and metadata
 */
export function useSessionStats(sessionData) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!sessionData) {
      setStats(null);
      return;
    }

    const calculateStats = () => {
      const { lapData, telemetry, capabilities, metadata } = sessionData;

      const basicStats = {
        totalLaps: lapData?.length || 0,
        fastestLap: lapData?.reduce((fastest, lap) => 
          (!fastest || lap.duration < fastest.duration) ? lap : fastest, null),
        totalDuration: lapData?.reduce((sum, lap) => sum + (lap.duration || 0), 0) || 0,
        avgLapTime: lapData?.length > 0 
          ? lapData.reduce((sum, lap) => sum + (lap.duration || 0), 0) / lapData.length 
          : 0
      };

      let telemetryStats = null;
      if (capabilities.hasTelemetry && telemetry?.locations) {
        const speeds = telemetry.locations
          .map(loc => loc.speed)
          .filter(speed => speed != null && !isNaN(speed));
        
        telemetryStats = {
          maxSpeed: speeds.length > 0 ? Math.max(...speeds) : 0,
          avgSpeed: speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0,
          dataPoints: telemetry.locations.length,
          duration: telemetry.totalDuration || 0
        };
      }

      return {
        basic: basicStats,
        telemetry: telemetryStats,
        capabilities,
        metadata
      };
    };

    setStats(calculateStats());
  }, [sessionData]);

  return stats;
} 